/**
 * Form Validation Utilities
 * Comprehensive validation functions for trading platform forms
 */

// Email validation with comprehensive regex
export const validateEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!email) return 'Email is required';
  if (!emailRegex.test(email)) return 'Please enter a valid email address';
  if (email.length > 254) return 'Email address is too long';
  
  return null;
};

// Password validation with security requirements
export const validatePassword = (password) => {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (password.length > 128) return 'Password is too long';
  if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
  if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
  if (!/\d/.test(password)) return 'Password must contain at least one number';
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return 'Password must contain at least one special character';
  }
  
  return null;
};

// Password confirmation validation
export const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword) return 'Please confirm your password';
  if (password !== confirmPassword) return 'Passwords do not match';
  
  return null;
};

// Name validation
export const validateName = (name, fieldName = 'Name') => {
  if (!name || !name.trim()) return `${fieldName} is required`;
  if (name.trim().length < 2) return `${fieldName} must be at least 2 characters long`;
  if (name.trim().length > 50) return `${fieldName} is too long`;
  if (!/^[a-zA-Z\s'-]+$/.test(name.trim())) {
    return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
  }
  
  return null;
};

// Phone number validation
export const validatePhoneNumber = (phone, required = false) => {
  if (!phone) return required ? 'Phone number is required' : null;
  
  // Remove all non-digit characters for validation
  const cleanPhone = phone.replace(/\D/g, '');
  
  if (cleanPhone.length < 10) return 'Phone number must be at least 10 digits';
  if (cleanPhone.length > 15) return 'Phone number is too long';
  
  return null;
};

// Trading amount validation
export const validateTradeAmount = (amount, balance = 0, minAmount = 1) => {
  if (!amount || amount === '') return 'Amount is required';
  
  const numAmount = parseFloat(amount);
  
  if (isNaN(numAmount)) return 'Please enter a valid number';
  if (numAmount <= 0) return 'Amount must be greater than 0';
  if (numAmount < minAmount) return `Minimum amount is $${minAmount}`;
  if (numAmount > balance) return 'Insufficient balance';
  if (numAmount > 1000000) return 'Amount exceeds maximum limit';
  
  // Check for too many decimal places
  const decimalPlaces = (amount.toString().split('.')[1] || '').length;
  if (decimalPlaces > 2) return 'Amount can have at most 2 decimal places';
  
  return null;
};

// Cryptocurrency address validation (basic)
export const validateCryptoAddress = (address, currency = 'BTC') => {
  if (!address) return 'Wallet address is required';
  
  const addressPatterns = {
    BTC: /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$|^bc1[a-z0-9]{39,59}$/,
    ETH: /^0x[a-fA-F0-9]{40}$/,
    LTC: /^[LM3][a-km-zA-HJ-NP-Z1-9]{26,33}$/,
    XRP: /^r[0-9a-zA-Z]{24,34}$/,
  };
  
  const pattern = addressPatterns[currency.toUpperCase()];
  if (!pattern) return 'Unsupported cryptocurrency';
  
  if (!pattern.test(address)) {
    return `Invalid ${currency.toUpperCase()} address format`;
  }
  
  return null;
};

// Credit card validation (basic Luhn algorithm)
export const validateCreditCard = (cardNumber) => {
  if (!cardNumber) return 'Card number is required';
  
  // Remove spaces and dashes
  const cleanNumber = cardNumber.replace(/[\s-]/g, '');
  
  if (!/^\d+$/.test(cleanNumber)) return 'Card number can only contain digits';
  if (cleanNumber.length < 13 || cleanNumber.length > 19) {
    return 'Card number must be between 13 and 19 digits';
  }
  
  // Luhn algorithm
  let sum = 0;
  let isEven = false;
  
  for (let i = cleanNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cleanNumber.charAt(i), 10);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  if (sum % 10 !== 0) return 'Invalid card number';
  
  return null;
};

// CVV validation
export const validateCVV = (cvv, cardType = 'visa') => {
  if (!cvv) return 'CVV is required';
  
  const isAmex = cardType.toLowerCase() === 'amex';
  const expectedLength = isAmex ? 4 : 3;
  
  if (!/^\d+$/.test(cvv)) return 'CVV can only contain digits';
  if (cvv.length !== expectedLength) {
    return `CVV must be ${expectedLength} digits`;
  }
  
  return null;
};

// Expiry date validation
export const validateExpiryDate = (expiryDate) => {
  if (!expiryDate) return 'Expiry date is required';
  
  const [month, year] = expiryDate.split('/');
  
  if (!month || !year) return 'Please enter date in MM/YY format';
  if (!/^\d{2}$/.test(month) || !/^\d{2}$/.test(year)) {
    return 'Please enter date in MM/YY format';
  }
  
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10) + 2000;
  
  if (monthNum < 1 || monthNum > 12) return 'Invalid month';
  
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  
  if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
    return 'Card has expired';
  }
  
  if (yearNum > currentYear + 20) return 'Expiry date is too far in the future';
  
  return null;
};

// Generic required field validation
export const validateRequired = (value, fieldName = 'Field') => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return `${fieldName} is required`;
  }
  return null;
};

// URL validation
export const validateURL = (url, required = false) => {
  if (!url) return required ? 'URL is required' : null;
  
  try {
    new URL(url);
    return null;
  } catch {
    return 'Please enter a valid URL';
  }
};

// Validation composer - validates multiple fields at once
export const validateForm = (formData, validationRules) => {
  const errors = {};
  
  Object.keys(validationRules).forEach(fieldName => {
    const value = formData[fieldName];
    const rules = validationRules[fieldName];
    
    for (const rule of rules) {
      const error = rule(value, formData);
      if (error) {
        errors[fieldName] = error;
        break; // Stop at first error for each field
      }
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Password strength calculator
export const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: 'Very Weak', color: '#ef4444' };
  
  let score = 0;
  const feedback = [];
  
  // Length
  if (password.length >= 8) score += 1;
  else feedback.push('Use at least 8 characters');
  
  if (password.length >= 12) score += 1;
  
  // Character types
  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('Add lowercase letters');
  
  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push('Add uppercase letters');
  
  if (/\d/.test(password)) score += 1;
  else feedback.push('Add numbers');
  
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 1;
  else feedback.push('Add special characters');
  
  // Avoid common patterns
  if (!/(.)\1{2,}/.test(password)) score += 1; // No repeated characters
  if (!/123|abc|qwe/i.test(password)) score += 1; // No sequences
  
  const strengthLevels = [
    { min: 0, max: 2, label: 'Very Weak', color: '#ef4444' },
    { min: 3, max: 4, label: 'Weak', color: '#f59e0b' },
    { min: 5, max: 6, label: 'Fair', color: '#eab308' },
    { min: 7, max: 7, label: 'Good', color: '#22c55e' },
    { min: 8, max: 8, label: 'Strong', color: '#10b981' }
  ];
  
  const strength = strengthLevels.find(level => score >= level.min && score <= level.max);
  
  return {
    score,
    maxScore: 8,
    label: strength.label,
    color: strength.color,
    feedback: feedback.slice(0, 3) // Limit feedback to top 3 items
  };
};

export default {
  validateEmail,
  validatePassword,
  validatePasswordConfirmation,
  validateName,
  validatePhoneNumber,
  validateTradeAmount,
  validateCryptoAddress,
  validateCreditCard,
  validateCVV,
  validateExpiryDate,
  validateRequired,
  validateURL,
  validateForm,
  calculatePasswordStrength
};